import { LOCAL_STORAGE_KEYS, MEDIA_CHANNEL, PAYMENT_TERM } from '$lib/utils/constants';
import { app } from '$lib/stores/app/store';
import { get } from 'svelte/store';

/**
 * @typedef {{[key: string]: string}} PaymentTermMap
 * A map of payment terms to LTV values
 */

/**
 * @typedef {{[key: string]: PaymentTermMap}} PlatformMap
 * A map of platforms to payment term maps
 */

/**
 * @typedef {{[key: string]: PlatformMap}} LTVMap
 * A map of media channels to platform maps
 */

/**
 * Service to resolve the LifeTime Value (LTV) for a given product and customer context
 */
class LTVService {
	/**
	 * The default LTV value to use when no other value is available
	 * @type {string}
	 */
	static DEFAULT_LTV = '74.00';

	/**
	 * The map of LTV values for each media channel and platform
	 * @type {LTVMap}
	 */
	static LTV_MAP = {
		[MEDIA_CHANNEL.AFFILIATE]: {
			browser: {
				[PAYMENT_TERM.ANNUAL]: '100.80',
				[PAYMENT_TERM.MONTHLY]: '65.05'
			}
		},
		[MEDIA_CHANNEL.FACEBOOK]: {
			browser: {
				[PAYMENT_TERM.ANNUAL]: '105.20',
				[PAYMENT_TERM.MONTHLY]: '72.92'
			}
		},
		[MEDIA_CHANNEL.GOOGLE_DISPLAY]: {
			browser: {
				[PAYMENT_TERM.ANNUAL]: '102.09',
				[PAYMENT_TERM.MONTHLY]: '73.07'
			}
		},
		[MEDIA_CHANNEL.INCENTIVIZED]: {
			browser: {
				[PAYMENT_TERM.ANNUAL]: '80.92',
				[PAYMENT_TERM.MONTHLY]: '39.16',
				[PAYMENT_TERM.SEMI_ANNUAL]: '59.21'
			}
		},
		[MEDIA_CHANNEL.INFLUENCERS]: {
			browser: {
				[PAYMENT_TERM.ANNUAL]: '106.41'
			}
		},
		[MEDIA_CHANNEL.NATIVE_APP]: {
			app_android: {
				[PAYMENT_TERM.ANNUAL]: '102.75',
				[PAYMENT_TERM.MONTHLY]: '77.61'
			},
			app_ios: {
				[PAYMENT_TERM.ANNUAL]: '90.83',
				[PAYMENT_TERM.MONTHLY]: '49.33'
			},
			app_other: {
				[PAYMENT_TERM.ANNUAL]: '120.60',
				[PAYMENT_TERM.MONTHLY]: '61.30'
			}
		},
		[MEDIA_CHANNEL.OTT]: {
			browser: {
				[PAYMENT_TERM.MONTHLY]: '57.01'
			}
		},
		[MEDIA_CHANNEL.OTHER_NON_PERFORMANCE]: {
			browser: {
				[PAYMENT_TERM.ANNUAL]: '111.85',
				[PAYMENT_TERM.MONTHLY]: '75.35'
			}
		},
		[MEDIA_CHANNEL.SEARCH_BRANDED]: {
			browser: {
				[PAYMENT_TERM.ANNUAL]: '105.27',
				[PAYMENT_TERM.BIENNIAL]: '110.81',
				[PAYMENT_TERM.MONTHLY]: '65.05'
			}
		},
		[MEDIA_CHANNEL.SEARCH_NON_BRANDED]: {
			browser: {
				[PAYMENT_TERM.ANNUAL]: '101.60',
				[PAYMENT_TERM.MONTHLY]: '67.63'
			}
		},
		[MEDIA_CHANNEL.THIRD_PARTY_EMAIL]: {
			browser: {
				[PAYMENT_TERM.ANNUAL]: '109.39',
				[PAYMENT_TERM.MONTHLY]: '89.58'
			}
		},
		[MEDIA_CHANNEL.UNKNOWN]: {
			browser: {
				[PAYMENT_TERM.ANNUAL]: '104.90',
				[PAYMENT_TERM.BIENNIAL]: '104.97',
				[PAYMENT_TERM.MONTHLY]: '74.81',
				[PAYMENT_TERM.SEMI_ANNUAL]: '100.63'
			}
		}
	};

	/**
	 * The patterns to use to determine the media channel for a given source tag
	 * @type {Array.<{patternStart: string, mediaChannel: string}>}
	 */
	static SOURCE_TAG_PATTERNS = [
		{
			patternStart: 'aff:di:',
			mediaChannel: MEDIA_CHANNEL.INCENTIVIZED
		},
		{
			patternStart: 'adjust::',
			mediaChannel: MEDIA_CHANNEL.NATIVE_APP
		},
		{
			patternStart: 'aff:',
			mediaChannel: MEDIA_CHANNEL.AFFILIATE
		},
		{
			patternStart: 'brand:',
			mediaChannel: MEDIA_CHANNEL.SEARCH_BRANDED
		},
		{
			patternStart: 'dis:',
			mediaChannel: MEDIA_CHANNEL.GOOGLE_DISPLAY
		},
		{
			patternStart: 'fb:',
			mediaChannel: MEDIA_CHANNEL.FACEBOOK
		},
		{
			patternStart: 'inf:',
			mediaChannel: MEDIA_CHANNEL.INFLUENCERS
		},
		{
			patternStart: 'ott:',
			mediaChannel: MEDIA_CHANNEL.OTT
		},
		{
			patternStart: 'sem:',
			mediaChannel: MEDIA_CHANNEL.SEARCH_NON_BRANDED
		},
		{
			patternStart: 'tpemail:',
			mediaChannel: MEDIA_CHANNEL.THIRD_PARTY_EMAIL
		}
	];

	/**
	 * Resolve the LTV mediaChannel for the current customer context
	 * @param {object} renewalInfo - information about the renewal cadence for a subscription
	 * @param {?number} renewalInfo.renewalPeriod - the number of renewal terms between subscription renewals
	 * @param {?string} renewalInfo.renewalTerm - the time term of the renewal period
	 * @param {object} productInfo - information about the product being purchased
	 * @param {string} productInfo.price - the price of the product
	 * @param {string} productInfo.currency - the ISO 4217 currency code of the product
	 * @returns {Promise<string>} - the LTV
	 */
	static async getLtv({ renewalPeriod, renewalTerm }, { price, currency }) {
		let ltv = LTVService.DEFAULT_LTV;
		if (renewalPeriod === null || renewalTerm === null) {
			// non-recurring product; use purchase price if USD, otherwise use default
			if (currency === 'USD') {
				ltv = price;
			}
		} else {
			ltv = await LTVService.getLtvFromMap(renewalPeriod, renewalTerm);
		}
		return ltv;
	}

	/**
	 * Get the LTV from the map
	 * @param {number} renewalPeriod - the number of renewal terms between subscription renewals
	 * @param {string} renewalTerm - the time term of the renewal period
	 * @returns {Promise<string>} - the LTV
	 */
	static async getLtvFromMap(renewalPeriod, renewalTerm) {
		const platform = await LTVService.getPlatform();
		const mediaChannel = await LTVService.getMediaChannel(platform);
		const paymentTerm = await LTVService.getPaymentTerm(renewalPeriod, renewalTerm);

		const ltv =
			LTVService.LTV_MAP[mediaChannel]?.[platform]?.[paymentTerm] ?? LTVService.DEFAULT_LTV;

		return ltv;
	}

	/**
	 * Get the payment term from the renewal period and renewal term
	 * @param {number} renewalPeriod - the number of renewal terms between subscription renewals
	 * @param {string} renewalTerm - the time term of the renewal period
	 * @returns {Promise<string>} - the payment term
	 */
	static async getPaymentTerm(renewalPeriod, renewalTerm) {
		const renewalTermLower = renewalTerm.toLowerCase();
		let paymentTerm = '';
		switch (renewalTermLower) {
			case 'day':
				if (renewalPeriod >= 28) {
					// only account for monthly payments, otherwise no match
					paymentTerm = PAYMENT_TERM.MONTHLY;
				}
				break;

			case 'week':
				if (renewalPeriod >= 4) {
					// only account for monthly payments, otherwise no match
					paymentTerm = PAYMENT_TERM.MONTHLY;
				}
				break;

			case 'month':
				if (renewalPeriod <= 3) {
					paymentTerm = PAYMENT_TERM.MONTHLY;
				} else if (renewalPeriod <= 6) {
					paymentTerm = PAYMENT_TERM.SEMI_ANNUAL;
				} else if (renewalPeriod < 18) {
					paymentTerm = PAYMENT_TERM.ANNUAL;
				} else {
					paymentTerm = PAYMENT_TERM.BIENNIAL;
				}
				break;

			case 'year':
				if (renewalPeriod <= 1) {
					paymentTerm = PAYMENT_TERM.ANNUAL;
				} else {
					paymentTerm = PAYMENT_TERM.BIENNIAL;
				}
				break;

			default:
				paymentTerm = '';
				break;
		}
		return paymentTerm;
	}

	/**
	 * Get the platform of the current customer
	 * @returns {Promise<string>} - the platform
	 */
	static async getPlatform() {
		const meta = get(app).meta;
		let platform = 'browser';

		if (meta.inApp) {
			if (meta.isIosApp) {
				platform = 'app_ios';
			} else if (meta.isAndroidApp) {
				platform = 'app_android';
			} else {
				platform = 'app_other';
			}
		}

		return platform;
	}

	/**
	 * Get the media channel for the current customer context
	 * @param {string} platform - the platform of the customer
	 * @returns {Promise<string>} - the media channel
	 */
	static async getMediaChannel(platform) {
		const sourceInfoJson = localStorage.getItem(LOCAL_STORAGE_KEYS.SOURCE_INFO) ?? '{}';
		const sourceInfo = JSON.parse(sourceInfoJson);
		const sourceTag = sourceInfo.id ?? '';

		let mediaChannel = MEDIA_CHANNEL.UNKNOWN;

		if (platform !== 'browser') {
			mediaChannel = MEDIA_CHANNEL.NATIVE_APP;
		} else {
			if (sourceTag !== '') {
				for (const pattern of LTVService.SOURCE_TAG_PATTERNS) {
					if (sourceTag.startsWith(pattern.patternStart)) {
						mediaChannel = pattern.mediaChannel;
						break;
					}
				}
				if (mediaChannel === MEDIA_CHANNEL.UNKNOWN) {
					// has a source tag but no explicit channel match
					mediaChannel = MEDIA_CHANNEL.OTHER_NON_PERFORMANCE;
				}
			}
		}
		return mediaChannel;
	}
}

export default LTVService;
